import {ChangeEvent, KeyboardEvent} from 'react';
import {Dispatch} from 'redux';
import {curry} from 'ramda';
import wrap from 'wraptile';
import {isEmptyObj, isEnter, isEmptyArr, isDefined} from '@pluto/utils';
import {
    FilterTypes,
    SearchHandler,
    ComponentStateType,
    SearchResetHandler,
    SearchMountHandler,
    ComponentFilterSetters,
    HandleRequestParamsType,
} from './SearchTypes';

const singleSelectFilters = [FilterTypes.contType, FilterTypes.status];

export const filterSelectedOut = curry((selected, e) => e !== selected);

const getNewValues = (
    isChecked: boolean,
    filterKey: FilterTypes,
    value: string,
    currValues: string[]
) => {
    const isSingleSelect = singleSelectFilters.includes(filterKey);

    if (isChecked) return [...(isSingleSelect ? [] : currValues), value];

    if (!isSingleSelect) return currValues.filter(filterSelectedOut(value));

    return [];
};

export const onFilter = wrap(
    (
        componentState: ComponentStateType,
        setFilterValue: Function,
        filterKey: FilterTypes,
        event: ChangeEvent<HTMLInputElement>,
        isChecked: boolean
    ) => {
        const {
            search,
            rowsPerPage,
            page,
            filters,
            onRequest,
            setSearchQuery,
        } = componentState;
        const {target} = event;
        const {value} = target;

        const newFilterValues = getNewValues(
            isChecked,
            filterKey,
            value,
            filters[filterKey]
        );

        setSearchQuery(search);
        setFilterValue(newFilterValues);

        const queryParams = {
            search,
            ...filters,
            [filterKey]: newFilterValues,
            limit: rowsPerPage,
            offset: page * rowsPerPage,
        };

        onRequest(queryParams);
    }
);

export const onFilterReset = wrap(
    (
        componentState: ComponentStateType,
        setFilterValue: Function,
        filterKey: FilterTypes
    ) => {
        const {
            search,
            rowsPerPage,
            page,
            onRequest,
            filters,
            setSearchQuery,
        } = componentState;

        setSearchQuery(search);
        setFilterValue([]);

        const queryParams = {
            search,
            ...filters,
            [filterKey]: [],
            limit: rowsPerPage,
            offset: page * rowsPerPage,
        };

        if (filters[filterKey].length) onRequest(queryParams);
    }
);

export const handleResetFilters = wrap((resetHandlers: ComponentFilterSetters) => {
    const {filtersSetters, clearFiltersInputs, setClearFiltersInputs} = resetHandlers;

    setClearFiltersInputs(!clearFiltersInputs);

    for (const fn of filtersSetters) {
        fn([]);
    }
});

export const handleResetSearch = wrap((resetHandler: Function) => {
    resetHandler('');
});

export const setInputValue = (setSearchValue: Function) => ({
    target: {value},
}: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(value);
};

export const handleRequest = wrap(
    (
        {setQuery, history, getSearchItems, route, dispatch}: SearchResetHandler,
        params: HandleRequestParamsType
    ) => {
        const filledParams = filterEmptyValues(params);

        setQuery(history, route, filledParams);

        const action = getSearchItems(filledParams);

        dispatch(action);
    }
);

export const handlePaging = curry((containerArgs, {limit, offset}) => {
    const {onRequest, ...containerParams} = containerArgs;

    onRequest({
        ...containerParams,
        limit,
        offset,
    });
});

export const onSearch = wrap((handlers: SearchHandler) => {
    const {search, onRequest, rowsPerPage, setSearchQuery, filters} = handlers;

    const queryParams = {
        search: search.trim(),
        offset: 0,
        limit: rowsPerPage,
        ...filters,
    };

    setSearchQuery(search);

    onRequest(queryParams);
});

export const onReset = wrap((handlers: SearchResetHandler) => {
    const {
        search,
        setPage,
        resetFilters,
        history,
        getSearchItems,
        limit,
        route,
        setQuery,
        dispatch,
    } = handlers;

    setPage(0);
    resetFilters();

    setQuery(history, route, {
        search,
    });

    const action = getSearchItems({
        offset: 0,
        limit,
        search,
    });

    dispatch(action);
});

export const filterEmptyValues = (params: HandleRequestParamsType) => {
    let output = {};
    for (const key in params) {
        const value = params[key];
        const isEmptyArray = Array.isArray(value) && isEmptyArr(value);

        if (isDefined(value) && !isEmptyArray) {
            output = {
                ...output,
                [key]: value,
            };
        }
    }

    return output;
};

export const handleMount = wrap((mountHandlers: SearchMountHandler) => {
    const {
        getContentPartners,
        getCategories,
        getGenres,
        contentPartnersLoading,
        categoriesLoading,
        genresLoading,
        getSearchItems,
        search,
        limit,
        offset,
        dispatch,
        filters,
    } = mountHandlers;

    if (contentPartnersLoading) {
        const action = getContentPartners();
        dispatch(action);
    }

    if (genresLoading) {
        const action = getGenres();
        dispatch(action);
    }

    if (categoriesLoading) {
        const action = getCategories();
        dispatch(action);
    }

    const filtersQuery = filterEmptyValues(filters);

    if (!isEmptyObj(filtersQuery) || search) {
        const action = getSearchItems({
            ...filtersQuery,
            search,
            offset,
            limit,
        });
        dispatch(action);
    }
});

export const handleKeyDown = curry(
    (handleSearch: () => void, {keyCode}: KeyboardEvent<HTMLInputElement>) => {
        if (isEnter(keyCode)) handleSearch();
    }
);

export const getFilterTitle = <T,>(headStr: string, list: T[]) =>
    `${headStr} ${list.length ? `(${list.length})` : ''}`;

export const handleUnmount = wrap((dispatch: Dispatch, action: Function) => () => {
    return dispatch(action());
});

const getFirstSplit = (splitter: string) => (str = ''): string => {
    return str.slice(0, str.indexOf(splitter));
};

const splitByDash = getFirstSplit('-');

export const getUUID = (id: string) => splitByDash(id).toUpperCase();
